import { resetForm, selectFields, selectForm, updateValue, updateValues } from '../formSlice'
import { errorMessage, finalExp } from 'utils/validation'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef, useState } from 'react'
import useTranslation from 'hooks/useTranslation'
import usePopupState from 'hooks/usePopupState'
import Select from 'components/inputs/Select'
import Submit from 'components/inputs/Submit'
import CompaniesPopup from './CompaniesPopup'
import Input from 'components/inputs/Input'
import { STATUS } from 'utils/constants'
import { findCompany } from 'rest/urls'
import { GET } from 'rest/request'

const Form = ({ block, submit, cancel }) => {
  const { companyData = '', ...form } = useSelector(selectForm)
  const fields = useSelector(selectFields)
  const [warning, setWarning] = useState({})
  const { popup, openPopup } = usePopupState()
  const nip = form.nip || ''

  const lastSearch = useRef('')

  const dispatch = useDispatch()
  const { t } = useTranslation('form')

  const refs = []
  const enter = t('enter')

  useEffect(() => {
    lastSearch.current = nip.length === 10 ? true : ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id])

  useEffect(() => {
    if (nip.length === 10 && companyData) lastSearch.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (lastSearch.current !== false && lastSearch.current !== nip && nip.length === 10) {
      document.activeElement.blur()

      if (lastSearch.current !== true) {
        dispatch(updateValues({ companyStatus: STATUS.pending }))
        openPopup('companies')
      }

      (async () => {
        try {
          const response = await GET(`${findCompany}${nip}`)
          dispatch(updateValues({
            companyData: Array.isArray(response?.companies) ? response.companies : response?.error,
            companyStatus: STATUS.succeed
          }))

        } catch (err) {
          dispatch(updateValues({ companyStatus: STATUS.failed }))
        }
      })()
    }

    if (nip.length === 10) lastSearch.current = nip
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, nip])

  const changeHandler = e => {
    if (!e) return
    const { name, value } = e.target
    dispatch(updateValue({ name, value }))
    if (
      name === 'nip'
        ? (warning.nip && value.match(finalExp.nip))
        : (warning[name] && value)
    ) {
      setWarning(prev => ({ ...prev, [name]: false }))
    }
  }

  const blurHandler = e => {
    if (!e) return
    const { name, value } = e.target

    if (
      name === 'nip'
        ? (warning.nip && value.match(finalExp.nip))
        : (warning[name] && value)
    ) {
      setWarning(prev => ({
        ...prev,
        [name]: name === 'nip'
          ? value.length < 10 ? errorMessage(t, 10 - value.length) : !value.match(finalExp.nip) ? t('incorrectTIN') : false
          : false
      }))
    }
  }

  const enterClicked = ref => {
    const index = refs.findIndex(r => Object.is(r, ref))
    if (index === -1) return
    if (refs[index + 1]) refs[index + 1].focus()
    else if (index + 1 === refs.length && !block) validation()
  }

  const validation = () => {
    const nipOK = !!nip.match(finalExp.nip)
    const formIsValid = nipOK && !fields.some((f) => f.isRequired && !form[f.inputName])

    if (formIsValid) {
      const keys = fields.map(({ inputName }) => inputName)
      const body = Object.entries(form).reduce((r, [k, v]) => {
        if (keys.includes(k)) r = { ...r, [k]: v }
        return r
      }, {})
      body.nip = nip
      lastSearch.current = ''
      form.id ? submit(body, form.id) : submit(body)

    } else {
      const newErrors = {
        nip: nip.length < 10 ? errorMessage(t, 10 - nip.length) : !nipOK ? t('incorrectTIN') : false
      }
      fields.forEach(f => {
        if (f.isRequired && !form[f.inputName]) {
          newErrors[f.inputName] = t('thisFieldIsRequired')
        }
      })
      setWarning(newErrors)
    }
  }

  const counterIcon = Array.isArray(companyData)
    ? <div
      className='element__icon'
      onClick={() => {
        openPopup('companies')
      }}>
      {companyData.length}
    </div>
    : undefined

  return (
    <>
      {popup === 'companies' && <CompaniesPopup
        changeNip={() => {
          if (refs[0]) refs[0]?.focus()
        }}
        selectData={({ raport = {}, ...data } = {}) => {
          const keys = fields.map(({ inputName }) => inputName)
          const values = Object.entries(data).reduce((r, [k, v]) => {
            if (keys.includes(k)) {
              const val = k === 'streetName' ? `${v} ${data?.homeNr}${data.flatNr ? `/${data.flatNr}` : ''}` : v
              r = { ...r, [k]: val }
            }
            return r
          }, {})

          if (raport.addressHeadCountryName) values['country'] = raport.addressHeadCountryName
          if (raport.detailLegalFormId) {
            const companyFormId = fields?.find(({ inputName }) => inputName === 'companyFormId')

            if (companyFormId && companyFormId.options) {
              const detailLegalFormId = Number(raport.detailLegalFormId)
              const option = companyFormId.options?.find(({ code }) => Number(code) === detailLegalFormId)

              if (option) values['companyFormId'] = option.value
            }
          }

          dispatch(updateValues(values))
        }}
      />}

      <div className='billing__form form'>
        <p className='form__title'>
          <span>{t(form.id ? 'editCompany' : 'addCompany')}</span>
        </p>

        <Input
          required
          ref={r => refs.push(r)}
          value={nip}
          name='nip'
          label={t('tin')}
          minLength='10'
          maxLength='10'
          icon={counterIcon}
          iconPosRight
          disabled={block}
          warning={warning.nip}
          placeholder={`${enter} ${t('tin')}`}
          enterClicked={enterClicked}
          changeHandler={changeHandler}
          blurHandler={blurHandler} />

        {nip.length === 10 && fields.length > 0 && fields?.map(({ id, isRequired, inputName, inputdefaultValue, inputType, inputLabel, placeholder, options }) => {
          if (inputdefaultValue && !form[inputName]) dispatch(updateValue({ name: inputName, value: inputdefaultValue }))
          return inputType === 'select' ? (
            <Select
              key={id}
              disabled={block}
              name={inputName}
              label={inputLabel}
              required={isRequired}
              warning={warning[inputName]}
              value={form[inputName] || ''}
              setValue={value => {
                changeHandler({ target: { value, name: inputName } })
              }}
              options={options}
            />
          ) : inputType === 'text' ? (
            <Input
              key={id}
              disabled={block}
              required={isRequired}
              ref={r => refs.push(r)}
              name={inputName}
              warning={warning[inputName]}
              placeholder={placeholder || `${enter} ${inputLabel}`}
              label={inputLabel}
              value={form[inputName] || ''}
              changeHandler={changeHandler}
              enterClicked={enterClicked}
            />
          ) : false
        })}

        {form.id ? <div className='center'>
          <button
            className='button --arrow --black --enlarged'
            onClick={() => !block && submit(null, form.id)}>
            <span className='--secFont'>{t('delete')}</span>
          </button>

          <button
            className='button --arrow --enlarged'
            onClick={() => !block && nip.length === 10 && validation()}>
            <span className='--secFont'>{t('change')}</span>
          </button>
        </div> :
          <Submit
            text={t('add')}
            action={() => !block && validation()} />}

        {cancel && <button
          className='button --back'
          onClick={() => {
            dispatch(resetForm())
            cancel()
          }}>
          <span>{t('cancel')}</span>
        </button>}
      </div>
    </>
  )
}

export default Form